import React, { useEffect, useState } from 'react';
import DefaultLayout from '../layout/DefaultLayout';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import beneficiosService from '../services/beneficiarioService';
import { FaLink } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumbs/Breadcrumb';

type BeneficioItem = {
    id: string;
    title: string;
    image?: string;
    details?: { text: string; url: string }[];
};

const Beneficios: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<BeneficioItem[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | undefined>(undefined);
    const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const result: any = await beneficiosService.getBeneficios();
        // result may be { success, data } and data could be JSON string or array
        if (result?.success) {
          let parsed: any = result.data;
          if (typeof parsed === 'string') {
            try {
              parsed = JSON.parse(parsed);
            } catch {
              // keep as string if parse fails
            }
          }
          // Some responses include a leading element like in mobile app; preserve behaviour: skip first item if needed
          if (Array.isArray(parsed)) {
            // mobile slice(1) behaviour seen previously — keep full list but if first is metadata (no title) skip it                
              const normalized = parsed.filter((b: any) => b && (b.title));
            setData(normalized);
          } else {
            setData([]);
          }
        } else {
          setError(result?.errors?.[0] ?? 'No se pudieron cargar los beneficios');
        }
      } catch (ex: any) {
        setError(ex?.message ?? 'Ocurrió un error al cargar los beneficios');
      } finally {
        setLoading(false);
      }
    };

        fetch();
    }, []);

    const openWebsite = (url?: string) => {
        if (!url) return;
        const ok = url.startsWith('http') ? url : `https://${url}`;
        window.open(ok, '_blank', 'noopener');
    };

    const renderList = () => {
        if (!data || data.length === 0)
            return <div className="bg-white rounded p-6 text-center text-gray-600">No se encontraron beneficios</div>;

        return (
            <>
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {data.map((b) => (
                        <button
                            key={b.id}
                            onClick={() => setSelectedId(b.id)}
                            className="bg-white dark:bg-boxdark rounded-lg shadow p-4 flex flex-col hover:shadow-md transition-shadow"
                            aria-label={`Abrir beneficio ${b.title}`}
                        >
                            {b.image ? (
                                <img
                                    src={b.image}
                                    alt={b.title}
                                    className="w-full h-40 object-contain rounded mb-3"
                                    onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                                />
                            ) : (
                                <div className="w-full h-40 bg-gray-100 rounded mb-3" />
                            )}
                            <div className="text-title-sm font-semibold text-gray-800">{b.title}</div>
                        </button>
                    ))}
                </div>
                <div className="flex gap-3 justify-center mt-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
                    >
                        Volver
                    </button>
                </div>
            </>

        );
    };

    const renderDetails = () => {
        const benefit = data?.find((d) => d.id === selectedId);
        if (!benefit) return null;

        return (
            <div className="space-y-4">

                {benefit.image && (
                    <img
                        src={benefit.image}
                        alt={benefit.title}
                        className="mx-auto w-auto h-auto max-w-full max-h-72 object-contain rounded-md"
                        onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                    />
                )}

                <h2 className="text-lg font-semibold">{benefit.title}</h2>

                <div className="space-y-3">
                    {(benefit.details || []).map((it, idx) => (
                        <div
                            key={idx}
                            className="dark:bg-boxdark flex items-center justify-between bg-white p-3 rounded shadow-sm hover:bg-gray-50 cursor-pointer"
                            onClick={() => openWebsite(it.url)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter') openWebsite(it.url); }}
                        >
                            <div className="text-title-sm text-gray-800">{it.text}</div>
                            <FaLink className="text-primario-celeste-800 ml-4" />
                        </div>
                    ))}
                </div>

                <div className="flex gap-3 justify-center mt-3">
                    <button
                        onClick={() => setSelectedId(null)}
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
                    >
                        Volver
                    </button>
                </div>
            </div>
        );
    };

    return (
        <DefaultLayout>

            <Breadcrumb pageName="Beneficios" />

            <div className="p-6">
                <div className="max-w-7xl mx-auto">


                    {loading && <Loading statusText="Cargando beneficios..." />}

                    {error && <Alert type="failure" message={error} />}

                    {!loading && !error && (
                        <div className="bg-gray-50 p-4 rounded">
                            {selectedId ? renderDetails() : renderList()}
                        </div>
                    )}
                </div>
            </div>
        </DefaultLayout>
    );
};

export default Beneficios;